import { useState, useEffect, useCallback, useReducer, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import usePrivilegesApi from '../../api/privileges/usePrivilegesApi';
import useUserPrivilegesApi from '../../api/users/useUserPrivilegesApi';
import useRolesApi from '../../api/roles/useRolesApi';
import {
  PRIVILEGE_TYPES,
  PRIVILEGE_CHANGE_STRATEGIES,
} from '../../../constants/role.constant';
import {
  groupPrivilegesBySections,
  computeSectionSelectionState,
  createBoxesState,
  computePrivilegeChanges,
  applyPrivilegeChanges,
  processCheckboxChangeWithReadEnforcement,
  initializeManuallyCheckedReads,
} from '../../utils/privilegesUtils';

const initialState = {
  loading: true,
  saving: false,
  privileges: [],
  sections: {},
  selectedPrivileges: [],
  boxesState: [],
  isSectionSelected: {},
  hasCustomPrivileges: false,
  initialPrivileges: [],
  manuallyCheckedReads: new Set(),
};

const actionTypes = {
  SET_LOADING: 'SET_LOADING',
  SET_SAVING: 'SET_SAVING',
  LOAD_DATA_SUCCESS: 'LOAD_DATA_SUCCESS',
  UPDATE_SELECTION: 'UPDATE_SELECTION',
  SET_CUSTOM_PRIVILEGES: 'SET_CUSTOM_PRIVILEGES',
  SET_INITIAL_PRIVILEGES: 'SET_INITIAL_PRIVILEGES',
};

const reducer = (state, action) => {
  switch (action.type) {
    case actionTypes.SET_LOADING:
      return { ...state, loading: action.payload };
    case actionTypes.SET_SAVING:
      return { ...state, saving: action.payload };
    case actionTypes.LOAD_DATA_SUCCESS:
      return {
        ...state,
        loading: false,
        privileges: action.payload.privileges,
        sections: action.payload.sections,
        selectedPrivileges: action.payload.selectedPrivileges,
        boxesState: action.payload.boxesState,
        isSectionSelected: action.payload.isSectionSelected,
        hasCustomPrivileges: action.payload.hasCustomPrivileges,
        initialPrivileges: action.payload.selectedPrivileges,
        manuallyCheckedReads: action.payload.manuallyCheckedReads || new Set(),
      };
    case actionTypes.UPDATE_SELECTION:
      return {
        ...state,
        selectedPrivileges: action.payload.selectedPrivileges,
        boxesState: action.payload.boxesState,
        isSectionSelected: action.payload.isSectionSelected,
        manuallyCheckedReads:
          action.payload.manuallyCheckedReads !== undefined
            ? action.payload.manuallyCheckedReads
            : state.manuallyCheckedReads,
      };
    case actionTypes.SET_CUSTOM_PRIVILEGES:
      return { ...state, hasCustomPrivileges: action.payload };
    case actionTypes.SET_INITIAL_PRIVILEGES:
      return { ...state, initialPrivileges: action.payload };
    default:
      return state;
  }
};

/**
 * Feature hook for managing editable privileges
 * @param {Object} options - Hook options
 * @param {string} options.userId - User ID
 * @param {string} options.privilegesUserId - User ID for privileges (if different)
 * @param {string} options.initialRoleId - Initial role ID
 * @param {Function} options.onSave - Save callback
 * @param {boolean} options.isInternal - Whether internal privileges (default: true)
 */
const useEditablePrivileges = ({
  userId,
  privilegesUserId,
  initialRoleId,
  initialCategoryId = '',
  onSave,
  isInternal = true,
}) => {
  const effectiveUserId = privilegesUserId || userId;
  const { t } = useTranslation();
  const [state, dispatch] = useReducer(reducer, initialState);
  const [selectedRoleId, setSelectedRoleId] = useState(initialRoleId);
  const [selectedCategoryId, setSelectedCategoryId] =
    useState(initialCategoryId);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingRoleChange, setPendingRoleChange] = useState(null);

  const { getPrivileges } = usePrivilegesApi();
  const { getGrantedPrivileges, hasCustomPrivileges } = useUserPrivilegesApi();
  const { getRolePrivileges } = useRolesApi();

  const roleGroupType = isInternal
    ? PRIVILEGE_TYPES.INTERNAL
    : PRIVILEGE_TYPES.EXTERNAL;

  const updateSelection = useCallback(
    (newSelectedIds, manualReads) => {
      const newBoxesState = createBoxesState(state.privileges, newSelectedIds);
      const newSectionState = computeSectionSelectionState(
        state.sections,
        newSelectedIds
      );

      dispatch({
        type: actionTypes.UPDATE_SELECTION,
        payload: {
          selectedPrivileges: newSelectedIds,
          boxesState: newBoxesState,
          isSectionSelected: newSectionState,
          manuallyCheckedReads: manualReads,
        },
      });
    },
    [state.privileges, state.sections]
  );

  useEffect(() => {
    const loadData = async () => {
      if (!effectiveUserId) return;

      dispatch({ type: actionTypes.SET_LOADING, payload: true });
      try {
        const [allPrivileges, userPrivilegesData, customCheck] =
          await Promise.all([
            getPrivileges(roleGroupType),
            getGrantedPrivileges(effectiveUserId),
            hasCustomPrivileges(effectiveUserId),
          ]);

        const sections = groupPrivilegesBySections(allPrivileges);
        const selectedIds = userPrivilegesData.map(
          (item) => item.Privilege?.id || item.privilege_id
        );
        const boxesState = createBoxesState(allPrivileges, selectedIds);
        const sectionState = computeSectionSelectionState(
          sections,
          selectedIds
        );

        dispatch({
          type: actionTypes.LOAD_DATA_SUCCESS,
          payload: {
            privileges: allPrivileges,
            sections,
            selectedPrivileges: selectedIds,
            boxesState,
            isSectionSelected: sectionState,
            hasCustomPrivileges: customCheck,
            manuallyCheckedReads: initializeManuallyCheckedReads(
              sections,
              selectedIds
            ),
          },
        });
      } catch (error) {
        console.error('Error loading privileges data:', error);
        dispatch({ type: actionTypes.SET_LOADING, payload: false });
      }
    };

    loadData();
  }, [
    effectiveUserId,
    roleGroupType,
    getPrivileges,
    getGrantedPrivileges,
    hasCustomPrivileges,
  ]);

  const applyRoleChangeReset = useCallback(
    async (newRoleId) => {
      setSelectedRoleId(newRoleId);
      if (newRoleId) {
        const rolePrivileges = await getRolePrivileges(newRoleId);
        const rolePrivilegeIds = rolePrivileges.map(
          (item) => item.Privilege?.id || item.privilege_id
        );
        updateSelection(rolePrivilegeIds);
      }
    },
    [getRolePrivileges, updateSelection]
  );

  const applyRoleChangeSync = useCallback(
    async (newRoleId) => {
      if (!newRoleId) return;

      const [oldRoleData, newRoleData] = await Promise.all([
        getRolePrivileges(selectedRoleId),
        getRolePrivileges(newRoleId),
      ]);

      const oldRolePrivileges = oldRoleData.map(
        (item) => item.Privilege?.id || item.privilege_id
      );
      const newRolePrivileges = newRoleData.map(
        (item) => item.Privilege?.id || item.privilege_id
      );

      const { added, removed } = computePrivilegeChanges(
        oldRolePrivileges,
        newRolePrivileges
      );
      const updatedPrivileges = applyPrivilegeChanges(
        state.selectedPrivileges,
        added,
        removed
      );

      setSelectedRoleId(newRoleId);
      updateSelection(updatedPrivileges);
    },
    [
      getRolePrivileges,
      selectedRoleId,
      state.selectedPrivileges,
      updateSelection,
    ]
  );

  const applyRoleChangeKeep = useCallback((newRoleId) => {
    setSelectedRoleId(newRoleId);
  }, []);

  const handleRoleChange = useCallback(
    async (newRoleId) => {
      if (state.hasCustomPrivileges && newRoleId !== selectedRoleId) {
        setPendingRoleChange(newRoleId);
        setShowConfirmModal(true);
      } else {
        await applyRoleChangeReset(newRoleId);
      }
    },
    [state.hasCustomPrivileges, selectedRoleId, applyRoleChangeReset]
  );

  const handleStrategySelect = useCallback(
    async (strategy) => {
      setShowConfirmModal(false);
      if (!pendingRoleChange) return;

      switch (strategy) {
        case PRIVILEGE_CHANGE_STRATEGIES.RESET:
          await applyRoleChangeReset(pendingRoleChange);
          dispatch({ type: actionTypes.SET_CUSTOM_PRIVILEGES, payload: false });
          break;
        case PRIVILEGE_CHANGE_STRATEGIES.SYNC:
          await applyRoleChangeSync(pendingRoleChange);
          break;
        case PRIVILEGE_CHANGE_STRATEGIES.KEEP:
          applyRoleChangeKeep(pendingRoleChange);
          break;
        default:
          break;
      }
      setPendingRoleChange(null);
    },
    [
      pendingRoleChange,
      applyRoleChangeReset,
      applyRoleChangeSync,
      applyRoleChangeKeep,
    ]
  );

  const handleCancelRoleChange = useCallback(() => {
    setShowConfirmModal(false);
    setPendingRoleChange(null);
  }, []);

  const handleCategoryChange = useCallback((newCategoryId) => {
    setSelectedCategoryId(newCategoryId);
  }, []);

  const handleResetToRole = useCallback(async () => {
    if (selectedRoleId) {
      const rolePrivileges = await getRolePrivileges(selectedRoleId);
      const rolePrivilegeIds = rolePrivileges.map(
        (item) => item.Privilege?.id || item.privilege_id
      );
      updateSelection(rolePrivilegeIds);
    }
  }, [selectedRoleId, getRolePrivileges, updateSelection]);

  const handleCheckboxChange = useCallback(
    (priv, index) => {
      const result = processCheckboxChangeWithReadEnforcement({
        priv,
        index,
        boxesState: state.boxesState,
        privileges: state.privileges,
        sections: state.sections,
        manuallyCheckedReads: state.manuallyCheckedReads,
      });

      if (!result) return;

      const { newBoxesState, newSelected, updatedManualReads } = result;
      const newSectionState = computeSectionSelectionState(
        state.sections,
        newSelected
      );

      dispatch({
        type: actionTypes.UPDATE_SELECTION,
        payload: {
          selectedPrivileges: newSelected,
          boxesState: newBoxesState,
          isSectionSelected: newSectionState,
          manuallyCheckedReads: updatedManualReads,
        },
      });
    },
    [
      state.boxesState,
      state.privileges,
      state.sections,
      state.manuallyCheckedReads,
    ]
  );

  const handleSectionSelection = useCallback(
    (category, resource) => {
      const resourceKey = `${category}-${resource}`;
      const sectionPrivileges = state.sections[category][resource];
      const isCurrentlySelected = state.isSectionSelected[resourceKey];

      let newSelected = [...state.selectedPrivileges];
      const newManualReads = new Set(state.manuallyCheckedReads);

      if (isCurrentlySelected) {
        sectionPrivileges.forEach((priv) => {
          newSelected = newSelected.filter((id) => id !== priv.id);
        });
        newManualReads.delete(resourceKey);
      } else {
        sectionPrivileges.forEach((priv) => {
          if (!newSelected.includes(priv.id)) {
            newSelected.push(priv.id);
          }
        });
        newManualReads.add(resourceKey);
      }

      updateSelection(newSelected, newManualReads);
    },
    [
      state.sections,
      state.isSectionSelected,
      state.selectedPrivileges,
      state.manuallyCheckedReads,
      updateSelection,
    ]
  );

  const handleSectionClearAll = useCallback(
    (category, resource) => {
      const resourceKey = `${category}-${resource}`;
      const sectionPrivileges = state.sections[category][resource];
      let newSelected = [...state.selectedPrivileges];

      sectionPrivileges.forEach((priv) => {
        newSelected = newSelected.filter((id) => id !== priv.id);
      });

      const newManualReads = new Set(state.manuallyCheckedReads);
      newManualReads.delete(resourceKey);

      updateSelection(newSelected, newManualReads);
    },
    [
      state.sections,
      state.selectedPrivileges,
      state.manuallyCheckedReads,
      updateSelection,
    ]
  );

  const handleSelectAll = useCallback(() => {
    if (state.selectedPrivileges.length === state.privileges.length) {
      updateSelection([], new Set());
    } else {
      const allIds = state.privileges.map((priv) => priv.id);
      const allManualReads = initializeManuallyCheckedReads(
        state.sections,
        allIds
      );
      updateSelection(allIds, allManualReads);
    }
  }, [
    state.selectedPrivileges.length,
    state.privileges,
    state.sections,
    updateSelection,
  ]);

  const restoreToSaved = useCallback(() => {
    updateSelection(
      state.initialPrivileges,
      initializeManuallyCheckedReads(state.sections, state.initialPrivileges)
    );
    setSelectedRoleId(initialRoleId);
    setSelectedCategoryId(initialCategoryId);
  }, [
    state.initialPrivileges,
    state.sections,
    updateSelection,
    initialRoleId,
    initialCategoryId,
  ]);

  const isDirty = useMemo(() => {
    if (state.loading) return false;
    const roleChanged = selectedRoleId !== initialRoleId;
    const privilegesChanged =
      JSON.stringify([...state.selectedPrivileges].sort()) !==
      JSON.stringify([...state.initialPrivileges].sort());
    const categoryChanged = selectedCategoryId !== initialCategoryId;
    return roleChanged || privilegesChanged || categoryChanged;
  }, [
    state.loading,
    selectedRoleId,
    initialRoleId,
    state.selectedPrivileges,
    state.initialPrivileges,
    selectedCategoryId,
    initialCategoryId,
  ]);

  const handleSave = useCallback(async () => {
    if (state.selectedPrivileges.length === 0) {
      toast.error(t('privilegesSection.minPrivilegeError'));
      return false;
    }

    dispatch({ type: actionTypes.SET_SAVING, payload: true });
    try {
      const roleChanged = selectedRoleId !== initialRoleId;
      const privilegesChanged =
        JSON.stringify([...state.selectedPrivileges].sort()) !==
        JSON.stringify([...state.initialPrivileges].sort());
      const categoryChanged = selectedCategoryId !== initialCategoryId;

      await onSave({
        roleId: selectedRoleId,
        privileges: state.selectedPrivileges,
        roleChanged,
        privilegesChanged,
        categoryId: selectedCategoryId,
        categoryChanged,
      });

      dispatch({
        type: actionTypes.SET_INITIAL_PRIVILEGES,
        payload: state.selectedPrivileges,
      });
      const customCheck = await hasCustomPrivileges(effectiveUserId);
      dispatch({
        type: actionTypes.SET_CUSTOM_PRIVILEGES,
        payload: customCheck,
      });
      return true;
    } catch (error) {
      console.error('Error saving:', error);
      return false;
    } finally {
      dispatch({ type: actionTypes.SET_SAVING, payload: false });
    }
  }, [
    state.selectedPrivileges,
    state.initialPrivileges,
    selectedRoleId,
    initialRoleId,
    initialCategoryId,
    onSave,
    hasCustomPrivileges,
    effectiveUserId,
    t,
    selectedCategoryId,
  ]);

  return {
    loading: state.loading,
    saving: state.saving,
    privileges: state.privileges,
    sections: state.sections,
    selectedPrivileges: state.selectedPrivileges,
    boxesState: state.boxesState,
    isSectionSelected: state.isSectionSelected,
    hasCustomPrivileges: state.hasCustomPrivileges,
    isDirty,
    selectedRoleId,
    selectedCategoryId,
    showConfirmModal,
    handleRoleChange,
    handleStrategySelect,
    handleCancelRoleChange,
    handleCategoryChange,
    handleResetToRole,
    handleCheckboxChange,
    handleSectionSelection,
    handleSectionClearAll,
    handleSelectAll,
    handleSave,
    restoreToSaved,
  };
};

export default useEditablePrivileges;
